import type { IUserInfo } from '../shared/models/identity/user-info';
import type { IPointOfSale } from '../shared/models/core/point-of-sale';
import { getOfflineCache, setOfflineCache } from './db';

interface OfflinePointOfSalesCache {
  pointOfSales: IPointOfSale[];
  defaultPointOfSaleId?: string;
  syncedAt: string;
}

const normalizeUserKey = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9@._-]+/g, '_');

export const getOfflineUserKey = (user: IUserInfo | null | undefined): string | undefined => {
  const value = user?.user_id || user?.sub || user?.email;
  return value ? normalizeUserKey(value) : undefined;
};

const cacheKey = (userKey: string) => `userPointOfSales:${userKey}`;

export const cacheUserPointOfSales = async (
  userKey: string | undefined,
  pointOfSales: IPointOfSale[],
  defaultPointOfSaleId?: string
): Promise<void> => {
  if (!userKey) return;

  await setOfflineCache<OfflinePointOfSalesCache>(cacheKey(userKey), {
    pointOfSales,
    defaultPointOfSaleId,
    syncedAt: new Date().toISOString(),
  });
};

export const getCachedUserPointOfSales = async (
  userKey: string | undefined
): Promise<OfflinePointOfSalesCache | undefined> => {
  if (!userKey) return undefined;
  return getOfflineCache<OfflinePointOfSalesCache>(cacheKey(userKey));
};

export const orderPointOfSalesForSync = (
  pointOfSales: IPointOfSale[],
  preferredPointOfSaleId?: string
): IPointOfSale[] => {
  const unique = Array.from(
    new Map(pointOfSales.map((pointOfSale) => [pointOfSale.id, pointOfSale])).values()
  );
  if (!preferredPointOfSaleId) return unique;

  const preferred = unique.find((pointOfSale) => pointOfSale.id === preferredPointOfSaleId);
  if (!preferred) return unique;

  return [preferred, ...unique.filter((pointOfSale) => pointOfSale.id !== preferred.id)];
};
